/**
 * The firm's own matter types — what it sells, and how each one runs.
 *
 * ===========================================================================
 * THE SEEDED GUESS THAT STUCK
 * ===========================================================================
 * Provisioning seeds exactly one service, "General matter", deliberately generic because
 * inventing practice areas for a firm nobody knows anything about would be guessing. The
 * catalogue then had no write surface and no screen, so the guess stuck: every matter at
 * every firm was a "General matter", and a firm could not describe its own practice.
 *
 * "General matter" is a default, not a fixture. It is renamed and edited here like any
 * other — its internal key stays put, because reporting keys on the key and a moved key
 * would split one service's history in two in every report that has ever run.
 *
 * ===========================================================================
 * RETIRING IS NOT DELETING, AND THE SCREEN HAS TO SAY SO
 * ===========================================================================
 * Matters point at a service, invoices reach it through them, and its name is what a
 * client reads on their own file. So there is no delete anywhere on this screen — there
 * is no verb for it on the resource either. Retiring stops anything NEW being started on
 * a matter type and changes nothing else, and it is refused outright while live matters
 * sit under it. `live_matter_count` is on the payload so the button is only offered when
 * it would work: a control that is offered and then refuses is a control people learn to
 * distrust.
 *
 * The work-gate explanations come from the server (`GET services/work-gates/`) rather
 * than being written here, so the explanation of a rule lives beside the rule.
 */

import {
  ConflictError,
  STAFF,
  qk,
  useApi,
  useApiMutation,
  useApiQuery,
  usePaginatedList,
  type ServiceDetail,
  type WorkGate,
  type WorkGateOption,
  type WorkflowSummary,
} from '@legalcrm/api';
import {
  Badge,
  Button,
  Form,
  FormActions,
  FormInput,
  FormSelect,
  FormTextarea,
  Modal,
  Table,
  useToast,
  useZodForm,
  type Column,
} from '@legalcrm/ui';
import { useState } from 'react';
import { z } from 'zod';

import { Can } from '@/auth/permissions';
import { humanise } from '@/lib/format';

const schema = z.object({
  name: z.string().min(1, 'Give the matter type a name'),
  description: z.string(),
  workflow: z.string(),
  work_gate: z.string().min(1, 'Choose when work is locked'),
});

type Values = z.infer<typeof schema>;

export function MatterTypesPage() {
  const [editing, setEditing] = useState<ServiceDetail | null>(null);
  const [creating, setCreating] = useState(false);

  // Retired ones included: this is the screen where somebody reinstates one, and a
  // matter type that has vanished from the only list that manages it is unrecoverable
  // from the interface.
  const list = usePaginatedList<ServiceDetail>(qk.list('services', { all: true }), STAFF.services, {
    limit: 100,
  });

  const gates = useApiQuery<WorkGateOption[]>(qk.list('work-gates'), STAFF.serviceWorkGates);
  const explain = (gate: WorkGate) =>
    gates.data?.find((option) => option.value === gate)?.explanation ?? '';

  const columns: Column<ServiceDetail>[] = [
    {
      key: 'name',
      header: 'Matter type',
      render: (row) => (
        <div className="min-w-0">
          <p className="truncate font-medium text-ink">{row.name}</p>
          {row.description && (
            <p className="truncate text-xs text-ink-muted">{row.description}</p>
          )}
        </div>
      ),
    },
    {
      key: 'workflow_name',
      header: 'Runs on',
      hideOnMobile: true,
      render: (row) => <span className="text-ink-muted">{row.workflow_name || '—'}</span>,
    },
    {
      key: 'work_gate',
      header: 'Work is locked until',
      render: (row) => (
        <div className="min-w-0">
          <p className="text-ink">{humanise(row.work_gate)}</p>
          <p className="text-xs text-ink-muted">{explain(row.work_gate)}</p>
        </div>
      ),
    },
    {
      key: 'is_active',
      header: 'In use',
      width: 'w-40',
      render: (row) =>
        row.is_active ? (
          <div className="flex flex-col items-start gap-1">
            <Badge tone="success" size="sm">
              In use
            </Badge>
            {row.live_matter_count > 0 && (
              <span className="text-xs text-ink-muted">
                {row.live_matter_count} live{' '}
                {row.live_matter_count === 1 ? 'matter' : 'matters'}
              </span>
            )}
          </div>
        ) : (
          <div className="flex flex-col items-start gap-1">
            <Badge tone="neutral" size="sm">
              Retired
            </Badge>
            {/* Said plainly, because "retired" is easily read as "deleted". */}
            <span className="text-xs text-ink-muted">Existing matters carry on</span>
          </div>
        ),
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      render: (row) => (
        <Can module="service" action="edit">
          <div className="flex justify-end gap-1">
            <Button variant="ghost" size="sm" onClick={() => setEditing(row)}>
              Edit
            </Button>
            <RetirementButton service={row} />
          </div>
        </Can>
      ),
    },
  ];

  return (
    <>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <p className="max-w-2xl text-sm text-ink-muted">
          Every matter is opened under one of these. The matter type decides which workflow
          the matter follows and when work on it is locked.
        </p>
        <Can module="service" action="create">
          <Button variant="primary" onClick={() => setCreating(true)}>
            New matter type
          </Button>
        </Can>
      </div>

      <Table
        caption="Matter types"
        columns={columns}
        rows={list.items}
        rowKey={(row) => row.id}
        loading={list.isLoading}
        error={list.error}
        onRetry={() => void list.refetch()}
        emptyTitle="No matter types"
        emptyDescription="This firm has nothing to open a matter under."
        pagination={{
          hasNext: list.hasNext,
          hasPrevious: list.hasPrevious,
          onNext: list.goNext,
          onPrevious: list.goPrevious,
        }}
      />

      <MatterTypeDialog
        open={creating}
        onClose={() => setCreating(false)}
        service={null}
        gates={gates.data ?? []}
      />
      <MatterTypeDialog
        open={editing !== null}
        onClose={() => setEditing(null)}
        service={editing}
        gates={gates.data ?? []}
      />
    </>
  );
}

/**
 * Retire, or put back.
 *
 * Disabled rather than hidden while matters are live under it, with the count as the
 * explanation: hiding it would leave somebody hunting for a control that exists, and
 * offering it would produce a 409 they could have been told about in advance.
 */
function RetirementButton({ service }: { service: ServiceDetail }) {
  const api = useApi();
  const { toast } = useToast();

  const change = useApiMutation<ServiceDetail, void>(
    {
      mutationFn: () =>
        api.post<ServiceDetail>(
          `${service.is_active ? STAFF.serviceRetire(service.id) : STAFF.serviceReinstate(service.id)}/`,
        ),
      invalidates: [qk.all('services')],
    },
    {
      onSuccess: (saved) =>
        toast({
          title: saved.is_active ? 'Back in use' : 'Retired',
          description: saved.is_active
            ? `${saved.name} can be chosen again.`
            : `Nothing new can be started on ${saved.name}. Matters already under it are untouched.`,
          tone: 'success',
        }),
      onError: (error) =>
        toast({
          title: 'The change was refused',
          description:
            error instanceof ConflictError ? error.detail : 'It could not be changed. Try again.',
          tone: 'warning',
        }),
    },
  );

  const blocked = service.is_active && service.live_matter_count > 0;

  return (
    <Button
      variant="ghost"
      size="sm"
      loading={change.isPending}
      disabled={blocked}
      title={
        blocked
          ? `${service.live_matter_count} live ${
              service.live_matter_count === 1 ? 'matter is' : 'matters are'
            } still under this. Close or complete them first — retiring stops new work starting, it does not stop work in progress.`
          : undefined
      }
      onClick={() => change.mutate()}
    >
      {service.is_active ? 'Retire' : 'Put back'}
    </Button>
  );
}

function MatterTypeDialog({
  open,
  onClose,
  service,
  gates,
}: {
  open: boolean;
  onClose: () => void;
  service: ServiceDetail | null;
  gates: WorkGateOption[];
}) {
  const api = useApi();
  const { toast } = useToast();

  // Only fetched while the dialog is open. A workflow is picked once, when a matter type
  // is created, and this is the only screen that asks.
  const workflows = useApiQuery<{ results: WorkflowSummary[] } | WorkflowSummary[]>(
    qk.list('workflows'),
    STAFF.workflows,
    { enabled: open },
  );
  const available = Array.isArray(workflows.data)
    ? workflows.data
    : (workflows.data?.results ?? []);

  const form = useZodForm(schema, {
    values: {
      name: service?.name ?? '',
      description: service?.description ?? '',
      workflow: service?.workflow ?? '',
      work_gate: service?.work_gate ?? 'agreement_signed',
    },
  });

  const chosenGate = form.watch('work_gate');
  const explanation = gates.find((option) => option.value === chosenGate)?.explanation ?? '';

  const save = useApiMutation<ServiceDetail, Values>(
    {
      mutationFn: (values) => {
        const body = {
          name: values.name,
          description: values.description,
          // Absent, never "". It is a UUID field, and an empty string is a 400. Left out
          // on create, the server picks the firm's default workflow.
          workflow: values.workflow || undefined,
          work_gate: values.work_gate,
        };
        return service
          ? api.patch<ServiceDetail>(`${STAFF.service(service.id)}/`, body)
          : api.post<ServiceDetail>(`${STAFF.services}/`, body);
      },
      invalidates: [qk.all('services')],
    },
    {
      onSuccess: (saved) => {
        toast({
          title: service ? 'Matter type updated' : 'Matter type added',
          description: saved.name,
          tone: 'success',
        });
        onClose();
      },
      onError: (error) =>
        toast({
          title: 'The change was refused',
          // The 409s name the thing to do: rename it, pick another workflow.
          description:
            error instanceof ConflictError ? error.detail : 'It could not be saved. Try again.',
          tone: 'warning',
        }),
    },
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={service ? 'Edit matter type' : 'New matter type'}
      description="Every matter opened under this follows the workflow you choose here, and its work gate decides when the firm may start work."
    >
      <Form
        form={form}
        ariaLabel={service ? 'Edit matter type' : 'New matter type'}
        error={save.error}
        onSubmit={(values) => save.mutateAsync(values)}
      >
        <FormInput
          form={form}
          name="name"
          label="Name"
          autoFocus
          autoComplete="off"
          hint="What a fee earner picks from, and what a client reads on their own file."
        />
        <FormTextarea
          form={form}
          name="description"
          label="Description"
          rows={2}
          hint="Optional. For colleagues, not for clients."
        />

        <FormSelect
          form={form}
          name="workflow"
          label="Workflow"
          options={[
            { value: '', label: available.length ? 'The firm’s default workflow' : 'Default' },
            ...available.map((workflow) => ({ value: workflow.id, label: workflow.name })),
          ]}
          hint={
            service
              ? 'Changing this affects matters opened from now on. Matters already running keep the workflow they started on.'
              : 'The stages a matter of this type moves through.'
          }
        />

        <FormSelect
          form={form}
          name="work_gate"
          label="Work is locked until"
          options={gates.map((option) => ({ value: option.value, label: option.label }))}
          hint={explanation}
        />

        {/* All four, together. Choosing between them means comparing them, and a hint
            that shows one line at a time makes somebody click through every option to
            find out what they are choosing between. */}
        <ul className="flex flex-col gap-1.5 rounded border bg-surface-sunken px-3 py-2 text-sm">
          {gates.map((option) => (
            <li key={option.value}>
              <span className="font-medium text-ink">{option.label}</span>
              <span className="text-ink-muted"> — {option.explanation}</span>
            </li>
          ))}
        </ul>

        <FormActions
          submitLabel={service ? 'Save matter type' : 'Add matter type'}
          busy={save.isPending}
          onCancel={onClose}
        />
      </Form>
    </Modal>
  );
}
